// The Keys screen's API keys (REQ-240; was the billing admin's Credentials tab): the org's keys,
// issued with a mandatory expiry and revoked from the key's summary (its row opens it) (API-010,
// REQ-013). A user key is what a developer puts in ANTHROPIC_AUTH_TOKEN; a service key carries
// scopes for an integrator. Both shells mount it with the org context they resolved; the proxy's
// calls go through nexus-web's forwarder. The secret is shown once, in the issue dialog, and
// nowhere else; a key is named by its prefix, never its id. While the API is unreachable the list
// still reads, and Issue and Revoke are off. A user key's person is picked by name from the org's
// active users; a person never types an id (IA-1): a failed read says so with Retry, and an org
// with no active users says that, with Issue off.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import { ApiError } from "../../api/errors";
import type { OrgContext } from "../../api/org-context";
import { useReadOnly } from "../../host/readonly";
import { ConfirmDialog, Dialog } from "../../primitives/dialog";
import { Checkbox, errorId, Field, Fieldset } from "../../primitives/field";
import { fieldErrorsFrom, hasErrors, type FieldErrors } from "../../primitives/forms";
import { Card, Chip } from "../../primitives/page";
import { RowDetail } from "../../primitives/row-detail";
import { Loading } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import { dayLabel } from "../usage/figures";
import {
  canRevokeCredential,
  credentialLabel,
  newCredentialRequest,
  openSecretDialog,
  SERVICE_CREDENTIAL_SCOPES,
  userOptions,
  validateNewCredential,
  type Credential,
  type CredentialKind,
  type NewCredentialValues,
  type SecretDialogState,
  type UserOption,
} from "./credentials-model";
import { SecretDialog } from "./secret-dialog";

/** The issue dialog's people: not read yet, being read, failed, or the active users by name. */
type PeopleState = { kind: "idle" } | { kind: "loading" } | { kind: "failed" } | { kind: "loaded"; options: UserOption[] };

const PEOPLE_FAILED = "Couldn't load people.";
const NO_PEOPLE = "No active users in this organization yet. A user key needs one; users appear after their first request through the proxy.";

const EMPTY_FORM: NewCredentialValues = { kind: "user_proxy", userId: "", expiresAt: "", scopes: [] };

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

export function Credentials({ context }: { context: OrgContext }) {
  const { orgId } = context;
  const client = useApi();
  const { readOnly } = useReadOnly();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewCredentialValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [secret, setSecret] = useState<SecretDialogState | null>(null);
  const [revoking, setRevoking] = useState<Credential | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [people, setPeople] = useState<PeopleState>({ kind: "idle" });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = (after: string | null, replace: boolean) => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/orgs/{orgId}/credentials", { path: { orgId }, query: after ? { cursor: after } : {} })
      .then((page) => {
        setCredentials((current) => (replace ? page.items : [...current, ...page.items]));
        setCursor(page.nextCursor);
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId]);

  // The user picker's options, read when the dialog first opens, and again on Retry.
  const loadPeople = () => {
    setPeople({ kind: "loading" });
    client
      .get("/v1/orgs/{orgId}/users", { path: { orgId }, query: { limit: 500, status: "active" } })
      .then((page) => setPeople({ kind: "loaded", options: userOptions(page.items) }))
      .catch(() => setPeople({ kind: "failed" }));
  };

  useEffect(() => {
    if (open && people.kind === "idle") loadPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A user key needs a person picked from the list; with no list, or an empty one, Issue is off.
  const noPerson = form.kind === "user_proxy" && !(people.kind === "loaded" && people.options.length > 0);

  const closeDialog = () => {
    setOpen(false);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  const submit = () => {
    const now = new Date();
    const validation = validateNewCredential(form, now);
    if (hasErrors(validation)) {
      setErrors(validation);
      return;
    }
    const body = newCredentialRequest(form, now);
    if (!body) return;
    setSubmitting(true);
    client
      .post("/v1/orgs/{orgId}/credentials", { path: { orgId }, body })
      .then((credential) => {
        setCredentials((current) => [credential, ...current]);
        setSecret(openSecretDialog(credential.secret));
        closeDialog();
      })
      .catch((err: unknown) => setErrors(fieldErrorsFrom(unexpected(err))))
      .finally(() => setSubmitting(false));
  };

  const confirmRevoke = () => {
    if (!revoking) return;
    const target = revoking;
    setBusyId(target.id);
    client
      .delete("/v1/orgs/{orgId}/credentials/{credentialId}", { path: { orgId, credentialId: target.id } })
      .then(() => {
        setCredentials((current) => current.map((row) => (row.id === target.id ? { ...row, revokedAt: new Date().toISOString() } : row)));
        setRevoking(null);
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setBusyId(null));
  };

  const selected = credentials.find((row) => row.id === selectedId) ?? null;
  const kindText = (row: Credential) => (row.kind === "service" ? "Service" : "User");
  const expiresText = (row: Credential) => (row.expiresAt ? dayLabel(row.expiresAt) : "Never");
  const lastUsedText = (row: Credential) => (row.lastUsedAt ? dayLabel(row.lastUsedAt) : "Never");
  const statusChip = (row: Credential) => <Chip tone={row.revokedAt ? "grey" : "green"}>{row.revokedAt ? "Revoked" : "Active"}</Chip>;

  const columns: Column<Credential>[] = [
    { key: "prefix", header: "Key", help: "customers.keyPrefix", cell: (row) => <span class="mono">{row.prefix}…</span> },
    { key: "kind", header: "Kind", help: "customers.keyKind", cell: kindText },
    {
      key: "expiresAt",
      header: "Expires",
      cell: expiresText,
      // A key that never expires sorts after every dated one.
      compare: (a, b) => (a.expiresAt ?? "~").localeCompare(b.expiresAt ?? "~"),
    },
    { key: "lastUsedAt", header: "Last used", cell: lastUsedText },
    { key: "revokedAt", header: "Status", cell: statusChip },
  ];

  const issueButton = (
    <button type="button" class="button" disabled={readOnly} onClick={() => setOpen(true)}>
      Issue API key
    </button>
  );

  return (
    <Card title="API keys" help="customers.apiKeys" actions={issueButton}>
      <p class="card-note">Keys for developers who cannot use AWS sign-in, and for integrations. Every key expires within a year.</p>
      <DataTable
        caption="API keys"
        hideCaption
        columns={columns}
        rows={credentials}
        rowKey={(row) => row.id}
        loading={loading}
        error={error}
        onRetry={() => load(null, true)}
        emptyMessage="No API keys. Developers who sign in with AWS need none."
        nextCursor={cursor}
        onLoadMore={() => load(cursor, false)}
        onRowClick={(row) => setSelectedId(row.id)}
        rowLabel={credentialLabel}
      />

      {selected && (
        <RowDetail
          title={`API key ${selected.prefix}…`}
          open
          onClose={() => setSelectedId(null)}
          facts={[
            { label: "Key", value: <span class="mono">{selected.prefix}…</span> },
            { label: "Kind", value: kindText(selected) },
            { label: "Expires", value: expiresText(selected) },
            { label: "Last used", value: lastUsedText(selected) },
            { label: "Status", value: statusChip(selected) },
          ]}
          actions={
            canRevokeCredential(selected) && (
              <button type="button" class="button button-danger" disabled={readOnly || busyId === selected.id} onClick={() => (setSelectedId(null), setRevoking(selected))}>
                Revoke
              </button>
            )
          }
        />
      )}

      <Dialog
        title="Issue API key"
        open={open}
        onClose={closeDialog}
        actions={
          <>
            <button type="button" class="button button-secondary" onClick={closeDialog}>
              Cancel
            </button>
            <button type="button" class="button button-primary" disabled={submitting || noPerson} onClick={submit}>
              {submitting ? "Issuing…" : "Issue"}
            </button>
          </>
        }
      >
        <div class="stack">
          <Field id="credential-kind" label="Kind" help="customers.keyKind">
            <select
              id="credential-kind"
              value={form.kind}
              onChange={(event) => setForm({ ...form, kind: event.currentTarget.value as CredentialKind, userId: "", scopes: [] })}
            >
              <option value="user_proxy">User (a developer's proxy key)</option>
              <option value="service">Service (an integration)</option>
            </select>
          </Field>
          {form.kind === "user_proxy" && people.kind === "loaded" && people.options.length > 0 && (
            <Field id="credential-user" label="User" help="customers.keyUser" error={errors["userId"]}>
              <select
                id="credential-user"
                value={form.userId}
                aria-invalid={errors["userId"] ? true : undefined}
                aria-describedby={errors["userId"] ? errorId("credential-user") : undefined}
                onChange={(event) => setForm({ ...form, userId: event.currentTarget.value })}
              >
                <option value="">Choose a user…</option>
                {people.options.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {form.kind === "user_proxy" && (people.kind === "idle" || people.kind === "loading") && <Loading label="Loading people…" rows={1} />}
          {form.kind === "user_proxy" && people.kind === "failed" && (
            <div class="banner banner-error" role="alert">
              <p>{PEOPLE_FAILED}</p>
              <button type="button" class="button" onClick={loadPeople}>
                Retry
              </button>
            </div>
          )}
          {form.kind === "user_proxy" && people.kind === "loaded" && people.options.length === 0 && (
            <p class="banner banner-info" role="note">
              {NO_PEOPLE}
            </p>
          )}
          {form.kind === "service" && (
            <Fieldset label="Scopes" help="customers.keyScopes" error={errors["scopes"]}>
              {SERVICE_CREDENTIAL_SCOPES.map((scope) => (
                <Checkbox
                  key={scope}
                  id={`credential-scope-${scope.replace(/[^a-z]/g, "-")}`}
                  label={scope}
                  help="customers.keyScopes"
                  checked={form.scopes.includes(scope)}
                  onChange={(checked) => setForm({ ...form, scopes: checked ? [...form.scopes, scope] : form.scopes.filter((s) => s !== scope) })}
                />
              ))}
            </Fieldset>
          )}
          <Field id="credential-expiry" label="Expires on" help="customers.keyExpiry" error={errors["expiresAt"]}>
            <input
              id="credential-expiry"
              type="date"
              value={form.expiresAt}
              aria-invalid={errors["expiresAt"] ? true : undefined}
              aria-describedby={errors["expiresAt"] ? errorId("credential-expiry") : undefined}
              onInput={(event) => setForm({ ...form, expiresAt: event.currentTarget.value })}
            />
          </Field>
        </div>
      </Dialog>

      <SecretDialog title="API key secret" id="credential-secret" secret={secret} onChange={setSecret} />

      <ConfirmDialog
        title="Revoke API key"
        open={revoking !== null}
        message={revoking ? `Revoking ${credentialLabel(revoking)} takes effect at proxies within their cache TTL.` : ""}
        confirmLabel="Revoke"
        typedName={revoking?.prefix ?? ""}
        busy={busyId === revoking?.id}
        onConfirm={confirmRevoke}
        onClose={() => setRevoking(null)}
      />
    </Card>
  );
}
